import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { TrendView } from "@/components/v2/TrendView";
import {
  DEFAULT_RECYCLING_TARGET,
  ViewType,
} from "@/components/v2/TrendView/recyclingRate/constants";
import { getPeriodLabelValueFromStartTime } from "@/components/v2/TrendView/utils/formatting";
import {
  getPeriodFilterValue,
  getPreviousYearFilterValue,
} from "@/components/v2/TrendView/utils/timeframe";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { FilterTimeFrameValue } from "server/config-api/index.js";
import { Granularity } from "server/dwhApi/common.js";

import { CardView } from "../../../../components/v2/CardView";
import { HomePageContext } from "../../context";
import { GRANULARITY_OPTIONS, PERIOD_OPTIONS } from "./constants";
import {
  getDataWithCurrentMonth,
  getRecyclingRateGraphData,
  getWasteAmountsGraphData,
  getWasteStreams,
} from "./utils";

export const BuildingTrendView: React.FC = () => {
  const { t } = useTrans();
  const { language } = useTrans();
  const { currentTerminal } = useTerminalsState();
  const { timezone } = timezoneStore();
  const { wasteTypes } = useCommonEntitiesStore();
  const { selectedTenantCategoryIds } = useContext(HomePageContext);
  const { wasteTypeConfig, terminalConfig, updateTerminalConfig } =
    useConfigService();
  const [showCompareYear, setShowCompareYear] = useState(false);
  const [showPortfolioAverage, setShowPortfolioAverage] = useState(false);

  const [granularity, setGranularity] = useState<Granularity>(
    Granularity.MONTH,
  );
  const [period, setPeriod] = useState<FilterTimeFrameValue>(
    FilterTimeFrameValue.Last12Months,
  );

  const granularityFilter = useMemo(
    () => ({
      options: GRANULARITY_OPTIONS,
      value: granularity,
      onChange: (key: string) => setGranularity(key as Granularity),
    }),
    [granularity],
  );

  const periodFilter = useMemo(
    () => ({
      options: PERIOD_OPTIONS,
      value: period,
      onChange: (key: string) => setPeriod(key as FilterTimeFrameValue),
    }),
    [period],
  );

  const periodFilterValue = useMemo(
    () => getPeriodFilterValue({ period, timezone }),
    [period, timezone],
  );

  const previousYearFilterValue = useMemo(
    () => getPreviousYearFilterValue({ periodFilterValue, period, timezone }),
    [period, periodFilterValue, timezone],
  );

  const { data: periodData, isLoading: isPeriodLoading } =
    trpc.dwh.terminals.getPeriods.useQuery(
      {
        ...periodFilterValue,
        timeZone: timezone,
        terminalId: currentTerminal?.id || "",
        granularity: granularity,
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            periodFilterValue.startTimeISO,
            periodFilterValue.endTimeISO,
            timezone,
            currentTerminal?.id,
            granularity,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal,
      },
    );

  const { data: periodDataComparison, isLoading: isPeriodComparisonLoading } =
    trpc.dwh.terminals.getPeriods.useQuery(
      {
        ...previousYearFilterValue,
        timeZone: timezone,
        terminalId: currentTerminal?.id || "",
        granularity: granularity,
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriodsComparison",
            previousYearFilterValue.startTimeISO,
            previousYearFilterValue.endTimeISO,
            timezone,
            currentTerminal?.id,
            granularity,
            period,
            selectedTenantCategoryIds,
          ].toString(),
        enabled:
          !!currentTerminal &&
          !!period &&
          period === FilterTimeFrameValue.Last12Months &&
          granularity === Granularity.MONTH &&
          !!showCompareYear,
      },
    );

  const { data: portfolioComparisonData, isLoading: isAggregatePeriodLoading } =
    trpc.dwh.terminals.getAggregatesPeriods.useQuery(
      {
        ...periodFilterValue,
        timeZone: timezone,
        granularity: granularity,
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalAggregatePeriods",
            periodFilterValue.startTimeISO,
            periodFilterValue.endTimeISO,
            timezone,
            granularity,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!showPortfolioAverage,
      },
    );

  const getPeriodLabelValue = useCallback(
    (startTime: string) =>
      getPeriodLabelValueFromStartTime({
        period,
        granularity,
        startTime,
        language,
        timezone,
      }),
    [granularity, language, period, timezone],
  );

  const dataWithCurrentMonth = useMemo(
    () => getDataWithCurrentMonth({ periodData, period }),
    [periodData, period],
  );

  const buildingTrendFilters = useMemo(
    () => [granularityFilter, periodFilter],
    [granularityFilter, periodFilter],
  );

  const showLoadingState = useMemo(
    () =>
      isPeriodLoading || isPeriodComparisonLoading || isAggregatePeriodLoading,
    [isPeriodLoading, isPeriodComparisonLoading, isAggregatePeriodLoading],
  );

  const comparisonYearBeforeData = useMemo(
    () =>
      period === FilterTimeFrameValue.Last12Months &&
      granularity === Granularity.MONTH
        ? periodDataComparison
        : undefined,
    [period, granularity, periodDataComparison],
  );

  const recyclingRateData = useMemo(
    () =>
      getRecyclingRateGraphData({
        data: dataWithCurrentMonth,
        getPeriodLabelValue,
        comparisonYearBeforeData,
        portfolioComparisonData,
      }),
    [
      dataWithCurrentMonth,
      getPeriodLabelValue,
      comparisonYearBeforeData,
      portfolioComparisonData,
    ],
  );

  const wasteAmountData = useMemo(
    () =>
      getWasteAmountsGraphData({
        data: dataWithCurrentMonth,
        getPeriodLabelValue,
        wasteTypes,
        wasteTypeConfig,
      }),
    [dataWithCurrentMonth, getPeriodLabelValue, wasteTypeConfig, wasteTypes],
  );

  const wasteStreams = useMemo(
    () =>
      getWasteStreams({
        data: dataWithCurrentMonth,
        wasteAmountData,
        wasteTypes,
        wasteTypeConfig,
      }),
    [dataWithCurrentMonth, wasteAmountData, wasteTypes, wasteTypeConfig],
  );

  const target = useMemo(
    () => terminalConfig?.recyclingTarget || DEFAULT_RECYCLING_TARGET,
    [terminalConfig],
  );

  const updateTarget = useCallback(
    (value: number) => {
      updateTerminalConfig({
        terminalId: currentTerminal?.id!,
        recyclingTarget: value,
      });
    },
    [updateTerminalConfig, currentTerminal],
  );

  return (
    <CardView
      title="homePage:buildingTrend"
      filters={buildingTrendFilters}
      isLoading={showLoadingState}
    >
      <TrendView
        period={period}
        granularity={granularity}
        showLoadingState={showLoadingState}
        recyclingRateData={recyclingRateData}
        wasteAmountsData={wasteAmountData}
        wasteAmountsStreams={wasteStreams}
        recyclingRateSubtitle={t("homePage:recyclingRateSubtitle", {
          buildingName: currentTerminal?.name,
          interpolation: { escapeValue: false },
        })}
        wasteAmountsSubtitle={t("homePage:wasteAmountSubtitle", {
          buildingName: currentTerminal?.name,
          interpolation: { escapeValue: false },
        })}
        viewType={ViewType.BUILDING}
        target={target}
        updateTarget={updateTarget}
        showCompareYear={showCompareYear}
        setShowCompareYear={setShowCompareYear}
        showPortfolioAverage={showPortfolioAverage}
        setShowPortfolioAverage={setShowPortfolioAverage}
      />
    </CardView>
  );
};
